import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { cn } from "@/lib/utils/cn";
import { useMountTransition } from "@/lib/utils/useMountTransition";
import { Icon } from "./Icon";
import { NAV_SECTIONS, isNavItemVisible, type NavItem } from "./nav-config";
import { useNavVisibility } from "./useNavVisibility";

/**
 * "Search here..." in the header - a quick jump to any page the signed-in
 * caller can actually reach, not a new backend search feature. Built
 * entirely from NAV_SECTIONS filtered through the exact same
 * isNavItemVisible/useNavVisibility rules Sidebar.tsx uses, so it can never
 * surface a route RBAC/feature toggles/Super Admin isolation would hide -
 * there is no separate "is this visible" check to drift out of sync with.
 */
export function QuickNavSearch() {
  const navigate = useNavigate();
  const ctx = useNavVisibility();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelOpen = open && query.trim() !== "";
  const { shouldRender, isVisible } = useMountTransition(panelOpen, 150);

  const allItems = useMemo(() => {
    const seen = new Set<string>();
    const items: NavItem[] = [];
    for (const section of NAV_SECTIONS) {
      for (const item of section.items) {
        const key = `${item.to}::${item.label}`;
        if (seen.has(key)) continue;
        seen.add(key);
        items.push(item);
      }
    }
    return items;
  }, []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allItems
      .filter((item) => isNavItemVisible(item, ctx))
      .filter((item) => item.label.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, allItems, ctx]);

  function go(item: NavItem) {
    navigate(item.to);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  }

  return (
    <div className="group relative hidden w-full max-w-sm sm:block">
      <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400 transition-colors duration-200 ease-out group-focus-within:text-primary-500">
        <Icon name="search" className="h-4 w-4" />
      </div>
      <input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && results[0]) go(results[0]);
          if (e.key === "Escape") {
            setOpen(false);
            inputRef.current?.blur();
          }
        }}
        placeholder="Search here..."
        aria-label="Search modules and pages"
        className="h-10 w-full rounded-full border border-slate-200 bg-slate-50 pl-9 pr-4 text-sm text-slate-700 placeholder:text-slate-400 transition-all duration-200 ease-out hover:border-slate-300 focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
      />

      {shouldRender && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className={cn(
              "absolute left-0 right-0 z-20 mt-2 max-h-80 origin-top overflow-y-auto scrollbar-thin rounded-xl border border-slate-200 bg-white py-1 shadow-soft transition-all duration-150 ease-out",
              isVisible ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0",
            )}
          >
            {results.length === 0 ? (
              <p className="px-4 py-3 text-sm text-slate-400">No matching pages.</p>
            ) : (
              results.map((item) => (
                <button
                  key={`${item.to}::${item.label}`}
                  type="button"
                  onClick={() => go(item)}
                  className={cn(
                    "flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-slate-700 transition-colors duration-150 ease-out hover:bg-primary-50/60 hover:text-primary-800",
                  )}
                >
                  <Icon name={item.icon} className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="truncate">{item.label}</span>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
